import React from 'react';
import { subscribe as subUsers, search as searchUsers, upsert as upsertUser, remove as removeUser, setStatus, assignRole, revokeRole, list as listUsers } from '../../../services/shared/userService';
import { subscribe as subAccess } from '../../../services/shared/accessService';

function Modal({ open, onClose, title, children, footer }){
  if(!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30">
      <div className="w-full max-w-xl bg-white rounded-lg shadow-lg border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-slate-900 font-semibold">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">×</button>
        </div>
        <div className="p-4">{children}</div>
        {footer && <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export default function UserManagement(){
  const [users, setUsers] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [status, setStatusFilter] = React.useState('all');
  const [role, setRoleFilter] = React.useState('all');
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ displayName: '', email: '', status: 'active', roles: [] });
  const [error, setError] = React.useState('');

  React.useEffect(()=> subUsers(setUsers), []);
  React.useEffect(()=> subAccess((s)=> setRoles(s.roles||[])), []);

  const filtered = React.useMemo(()=> searchUsers({ q, status, role }), [users, q, status, role]);

  function startCreate(){ setEditing(null); setForm({ displayName:'', email:'', status:'active', roles: [] }); setError(''); setOpen(true); }
  function startEdit(u){ setEditing(u); setForm({ displayName:u.displayName, email:u.email, status:u.status, roles: u.roles||[] }); setError(''); setOpen(true); }

  function save(){
    try{
      const payload = editing ? { ...editing, ...form } : { ...form };
      upsertUser(payload);
      setOpen(false);
    }catch(e){ setError(e.message || 'Failed to save'); }
  }

  function onAssignRole(u, roleId){ assignRole(u.id, roleId); }
  function onRevokeRole(u, roleId){ revokeRole(u.id, roleId); }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <div className="flex gap-2">
            <input value={q} onChange={e=> setQ(e.target.value)} placeholder="Search users…" className="h-9 px-3 rounded-md border border-slate-300 text-sm" />
            <select value={status} onChange={e=> setStatusFilter(e.target.value)} className="h-9 px-2 rounded-md border border-slate-300 text-sm">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <select value={role} onChange={e=> setRoleFilter(e.target.value)} className="h-9 px-2 rounded-md border border-slate-300 text-sm">
              <option value="all">All Roles</option>
              {roles.map(r=> <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <button onClick={startCreate} className="h-9 px-3 rounded-md bg-purple-600 text-white text-sm hover:bg-purple-500">Add User</button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-600">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Roles</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u=> (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="py-2 pr-3">{u.displayName}</td>
                  <td className="py-2 pr-3">{u.email}</td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles||[]).map(rid=> {
                        const r = roles.find(x=> x.id === rid);
                        return (
                          <span key={rid} className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-xs bg-slate-100 border border-slate-200 text-slate-700">
                            {r ? r.name : rid}
                            <button onClick={()=> onRevokeRole(u, rid)} className="text-slate-500 hover:text-slate-700">×</button>
                          </span>
                        );
                      })}
                      <select onChange={(e)=>{ const id = e.target.value; if(id){ onAssignRole(u, id); e.target.value=''; } }} className="h-6 px-1 rounded border border-slate-300 text-xs">
                        <option value="">+ Add role</option>
                        {roles.filter(r=> !(u.roles||[]).includes(r.id)).map(r=> <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <span className={"inline-flex items-center px-2 h-6 rounded-full text-xs border " + (u.status==='active' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700')}>{u.status}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <button onClick={()=> startEdit(u)} className="px-2 h-8 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Edit</button>
                      {u.status==='active' ? (
                        <button onClick={()=> setStatus(u.id, 'suspended')} className="px-2 h-8 rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50">Suspend</button>
                      ) : (
                        <button onClick={()=> setStatus(u.id, 'active')} className="px-2 h-8 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Activate</button>
                      )}
                      <button onClick={()=> removeUser(u.id)} className="px-2 h-8 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length===0 && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-500">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={()=> setOpen(false)} title={editing? 'Edit User' : 'Add User'}
        footer={(
          <>
            <button onClick={()=> setOpen(false)} className="h-9 px-3 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={save} className="h-9 px-3 rounded-md bg-purple-600 text-white hover:bg-purple-500">Save</button>
          </>
        )}
      >
        {error && <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-rose-800 text-sm">{error}</div>}
        <div className="grid grid-cols-1 gap-3">
          <label className="text-sm">
            <div className="text-slate-700 mb-1">Display Name</div>
            <input value={form.displayName} onChange={e=> setForm(f=> ({...f, displayName: e.target.value}))} className="w-full h-9 px-3 rounded-md border border-slate-300" />
          </label>
          <label className="text-sm">
            <div className="text-slate-700 mb-1">Email</div>
            <input value={form.email} onChange={e=> setForm(f=> ({...f, email: e.target.value}))} className="w-full h-9 px-3 rounded-md border border-slate-300" />
          </label>
          <label className="text-sm">
            <div className="text-slate-700 mb-1">Status</div>
            <select value={form.status} onChange={e=> setForm(f=> ({...f, status: e.target.value}))} className="w-full h-9 px-2 rounded-md border border-slate-300">
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </label>
        </div>
      </Modal>
    </div>
  );
}
